/**
 * Home v2 — broadcast → notice mapping.
 *
 * Two things the drawer wants do not exist in the data yet:
 *
 *  1. **Severity / category.** `PlatformBroadcast` has no such column, so every
 *     notice maps to the neutral "produto" tone. The critical/financeiro tags
 *     in the design cannot be honoured until the schema carries them — which is
 *     precisely why the spec proposes adding both as an additive migration.
 *  2. **Read state.** `GET /history` returns the broadcast rows without joining
 *     `PlatformBroadcastAck`, so nothing here can tell read from unread. Until
 *     the endpoint includes it, every item renders as unread.
 *
 * Deriving these from title keywords was the alternative and was rejected: a
 * heuristic that silently mislabels a financial notice as a product update is
 * worse than an honest neutral tag.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function relativeWhen(iso) {
  if (!iso) return "";
  const delta = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(delta)) return "";
  if (delta < HOUR) {
    const minutes = Math.max(1, Math.round(delta / MINUTE));
    return `há ${minutes} min`;
  }
  if (delta < DAY) {
    const hours = Math.round(delta / HOUR);
    return hours === 1 ? "há 1 hora" : `há ${hours} horas`;
  }
  const days = Math.round(delta / DAY);
  if (days === 1) return "ontem";
  if (days < 7) return `${days} dias`;
  const weeks = Math.round(days / 7);
  return weeks === 1 ? "1 semana" : `${weeks} semanas`;
}

/** Strips markdown noise so the drawer can show a plain one-line teaser. */
export function toSummary(contentMd, limit = 160) {
  if (!contentMd) return "";
  const plain = String(contentMd)
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_`~-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > limit ? `${plain.slice(0, limit).trimEnd()}…` : plain;
}

export function mapBroadcastToNotice(broadcast) {
  return {
    id: broadcast.id,
    // See the module note: no severity column exists yet.
    tone: "produto",
    tagLabel: "COMUNICADO",
    when: relativeWhen(broadcast.createdAt),
    title: broadcast.title,
    summary: toSummary(broadcast.contentMd),
    cta: broadcast.ctaButton?.label || "Abrir comunicado",
    // See the module note: /history does not join the ack table.
    read: false,
  };
}

export function mapBroadcastsToNotices(items = []) {
  return items.map(mapBroadcastToNotice);
}
